import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  startOfMonth, endOfMonth, startOfWeek, endOfWeek, eachDayOfInterval, format,
  isSameMonth, isToday, addMonths, subMonths, parseISO,
} from "date-fns";
import { es } from "date-fns/locale";
import { ChevronLeft, ChevronRight, Loader2 } from "lucide-react";

const TYPE_COLOR = {
  blog: "bg-[#0055FF]/10 text-[#0055FF]", service_page: "bg-[#8B5CF6]/10 text-[#8B5CF6]",
  location_page: "bg-[#F59E0B]/10 text-[#92400E]",
  facebook: "bg-[#1877F2]/10 text-[#1877F2]", instagram: "bg-[#E1306C]/10 text-[#E1306C]", twitter: "bg-[#0F1419]/10 text-[#0F1419]",
  nextdoor: "bg-[#8ED500]/15 text-[#4D7A00]", linkedin: "bg-[#0A66C2]/10 text-[#0A66C2]",
};

const ASSISTED_TOOLTIP = "Nextdoor no permite que otras herramientas publiquen automáticamente. La plataforma te deja el post listo y vos lo publicás con un toque.";

export default function CalendarPage() {
  const { activeTenantId } = useAuth();
  const [month, setMonth] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [platformFilter, setPlatformFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");

  const load = useCallback(async (d) => {
    setLoading(true);
    const start = startOfWeek(startOfMonth(d));
    const end = endOfWeek(endOfMonth(d));
    try {
      const res = await api.get("/calendar/events", { params: { start: start.toISOString(), end: end.toISOString() } });
      setEvents(res.data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (activeTenantId) load(month); }, [activeTenantId, month, load]);

  const days = eachDayOfInterval({ start: startOfWeek(startOfMonth(month)), end: endOfWeek(endOfMonth(month)) });

  const filtered = events.filter((e) => {
    if (platformFilter !== "all" && (e.platform || e.content_type) !== platformFilter) return false;
    if (statusFilter !== "all" && e.status !== statusFilter) return false;
    return true;
  });

  const eventsForDay = (day) => filtered.filter((e) => format(parseISO(e.scheduled_at), "yyyy-MM-dd") === format(day, "yyyy-MM-dd"));

  const onDrop = async (e, day) => {
    e.preventDefault();
    const raw = e.dataTransfer.getData("text/plain");
    if (!raw) return;
    const { event_type, id, scheduled_at } = JSON.parse(raw);
    const original = parseISO(scheduled_at);
    const newDate = new Date(day);
    newDate.setHours(original.getHours(), original.getMinutes());
    try {
      await api.put(`/calendar/events/${event_type}/${id}/reschedule`, { scheduled_at: newDate.toISOString() });
      toast.success("Reprogramado");
      load(month);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo reprogramar (revisá el estado del contenido)");
    }
  };

  return (
    <div className="space-y-6" data-testid="calendar-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={() => setMonth((m) => subMonths(m, 1))} data-testid="calendar-prev-month" className="rounded-md h-8 w-8">
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <h2 className="font-heading text-lg font-bold capitalize w-40 text-center">{format(month, "MMMM yyyy", { locale: es })}</h2>
          <Button variant="outline" size="icon" onClick={() => setMonth((m) => addMonths(m, 1))} data-testid="calendar-next-month" className="rounded-md h-8 w-8">
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
        <div className="flex gap-2">
          <Select value={platformFilter} onValueChange={setPlatformFilter}>
            <SelectTrigger className="w-40 h-8 text-xs" data-testid="calendar-platform-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas las redes/tipos</SelectItem>
              <SelectItem value="facebook">Facebook</SelectItem>
              <SelectItem value="instagram">Instagram</SelectItem>
              <SelectItem value="twitter">Twitter/X</SelectItem>
              <SelectItem value="linkedin">LinkedIn</SelectItem>
              <SelectItem value="blog">Blog</SelectItem>
              <SelectItem value="service_page">Página de servicio</SelectItem>
              <SelectItem value="location_page">Página de ubicación</SelectItem>
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-36 h-8 text-xs" data-testid="calendar-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los estados</SelectItem>
              <SelectItem value="draft">Borrador</SelectItem>
              <SelectItem value="scheduled">Programado</SelectItem>
              <SelectItem value="published">Publicado</SelectItem>
              <SelectItem value="failed">Fallido</SelectItem>
              <SelectItem value="pending_manual">Asistida (pendiente)</SelectItem>
              <SelectItem value="expired_unpublished">Vencida — no se publicó</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-[#0055FF]" /></div>
      ) : (
        <Card className="rounded-sm border-[#E4E4E7] shadow-none overflow-hidden">
          <div className="grid grid-cols-7 border-b border-[#E4E4E7] bg-[#FAFAFA]">
            {["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"].map((d) => (
              <div key={d} className="text-[10px] uppercase tracking-wider text-[#71717A] font-medium p-2 text-center">{d}</div>
            ))}
          </div>
          <div className="grid grid-cols-7" data-testid="calendar-grid">
            {days.map((day) => {
              const dayEvents = eventsForDay(day);
              return (
                <div key={day.toISOString()} onDragOver={(e) => e.preventDefault()} onDrop={(e) => onDrop(e, day)}
                  data-testid={`calendar-day-${format(day, "yyyy-MM-dd")}`}
                  className={`min-h-24 border-b border-r border-[#F4F4F5] p-1.5 ${isSameMonth(day, month) ? "" : "bg-[#FAFAFA]/50 text-[#A1A1AA]"}`}>
                  <p className={`text-xs mb-1 ${isToday(day) ? "font-bold text-[#0055FF]" : ""}`}>{format(day, "d")}</p>
                  <div className="space-y-1">
                    {dayEvents.map((ev) => (
                      <div key={`${ev.event_type}-${ev.id}`} draggable
                        onDragStart={(e) => e.dataTransfer.setData("text/plain", JSON.stringify(ev))}
                        data-testid={`calendar-event-${ev.event_type}-${ev.id}`}
                        className={`text-[10px] px-1.5 py-0.5 rounded-sm truncate cursor-grab flex items-center gap-1 ${TYPE_COLOR[ev.platform || ev.content_type] || "bg-[#71717A]/10 text-[#71717A]"}`}
                        title={ev.is_manual ? `${ev.title} — Asistida: ${ASSISTED_TOOLTIP}` : ev.title}>
                        {ev.is_manual && (
                          <span className="uppercase tracking-wider shrink-0 font-medium" data-testid={`calendar-assisted-tag-${ev.id}`}>Asistida</span>
                        )}
                        {ev.auto_scheduled && (
                          <span className="uppercase tracking-wider shrink-0 font-medium opacity-70" data-testid={`calendar-auto-tag-${ev.id}`} title="Programada automáticamente">Auto</span>
                        )}
                        <span className="truncate">{ev.title}</span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      )}
    </div>
  );
}
